import { TextConstants } from '../text-constants';

export class TextHelper {
  static canvas: HTMLCanvasElement;

  static removeDiacritics(value: string): string {
    return value?.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  static getTextWidth(text: string, font?: string, margin = 8): number {
    if (!text) {
      return 0;
    }
    const canvas = TextHelper.canvas || (TextHelper.canvas = document.createElement('canvas'));
    const context = canvas.getContext('2d');
    context.font = font || 'normal 14px sans-serif';
    const metrics = context.measureText(text);
    return metrics.width + 2 * margin + 4;
  }

  // https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript
  // get element's font style and size and return it as a string -> to be used for getTextWidth method
  static getCanvasFont(el = document.body): string {
    function getCssStyle(el: HTMLElement, style: string): string {
      return window.getComputedStyle(el, null).getPropertyValue(style);
    }
    const fontWeight = getCssStyle(el, 'font-weight') || 'normal';
    const fontSize = getCssStyle(el, 'font-size') || '14x';
    const fontFamily = getCssStyle(el, 'font-family') || 'sans-serif';

    return `${fontWeight} ${fontSize} ${fontFamily}`;
  }

  // https://www.geeksforgeeks.org/string-hashing-using-polynomial-rolling-hash-function/
  static polynomialRollingHash(str: string): number {
    const p = 31;
    const m = 1e9 + 9;
    let powerOfP = 1;
    let hashVal = 0;
    for (let i = 0; i < str.length; i++) {
      hashVal = (hashVal + (str.charCodeAt(i) - 'a'.charCodeAt(0) + 1) * powerOfP) % m;
      powerOfP = (powerOfP * p) % m;
    }
    return hashVal;
  }

  // shorten text and add '...' at the end of shortening text
  static textEllipsis(str: string, length: number): string {
    if (str == null) {
      return null;
    }
    if (!length || str.length <= length) {
      return str;
    }
    const indexN = str.indexOf('\n');
    length = indexN > -1 && indexN < length ? indexN : length;
    const indexR = str.indexOf('\r');
    length = indexR > -1 && indexR < length ? indexR : length;
    return str.substring(0, length).concat('...');
  }

  static toPascalCase(kebebCase: string): string {
    if (!kebebCase) {
      return kebebCase;
    }
    const parts = kebebCase.split('-');
    const pascalCase = parts.map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join('');
    return pascalCase;
  }

  static getUrlName(name: string): string {
    return name
      .replace(/([A-Z])/g, (a: string, b: string) => `-${b}`)
      .replace(/([A-Z])-([A-Z])-([A-Z])/g, (a: string, b: string, c: string, d: string) => `${b}${c}-${d}`)
      .substring(1)
      .toLowerCase();
  }

  static getUrlNameFromLabel(label: string): string {
    return this.removeDiacritics(label)
      ?.toLowerCase()
      .replace(/[^a-z0-9 ]/g, '')
      .replace(/ /g, '-')
      .replace(/(?<=-)-/g, '')
      .replace(/^-+|-+$/g, '');
  }

  static firstLetterToUpperCase(text: string): string {
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
  }

  static firstLetterToLowerCase(text: string): string {
    return `${text.charAt(0).toLowerCase()}${text.slice(1)}`;
  }

  static joinWithCommaAndOr(texts: string[]): string {
    const parts: string[] = [];
    texts.forEach((t, i) => (parts[i * 2] = t));
    texts.filter((t, i) => i < texts.length - 1).forEach((t, i) => (parts[i * 2 + 1] = i < texts.length - 2 ? ', ' : ' or '));
    return parts.join('');
  }

  static convertSpacesToNbsp(value: string): string {
    return value.replace(/ /g, '\u00A0');
  }

  static convertNbspToSpaces(value: string): string {
    return value.replace(/\u00A0/g, ' ');
  }

  static getTextInRightForm(count: number, text: string, textInPlural: string = null): string {
    return count === 1 ? text : (textInPlural ?? `${text}s`);
  }

  static getTextWithCount(count: number, text: string, textInPlural: string = null, includeCount = true): string {
    const validTextForm = this.getTextInRightForm(count, text, textInPlural);
    return includeCount ? `${count} ${validTextForm}` : validTextForm;
  }

  static enumToText(value: string): string {
    if (value == null) {
      return null;
    }
    if (value === TextConstants.notAvailable) {
      return TextConstants.notAvailable;
    }
    const parts = value.split(/(?=[A-Z])/);
    return this.firstLetterToUpperCase(parts.join(' ').toLowerCase());
  }

  static splitText(text: string, maxLength: number): string[] {
    const lines: string[] = [];
    while (text.length > 0) {
      const index = text.indexOf(' ', maxLength);
      if (index === -1) {
        lines.push(text);
        break;
      }

      lines.push(text.substring(0, index));
      text = text.substring(index + 1);
    }

    return lines;
  }

  static removeHtmlTags(text: string): string {
    return text?.replace(/<[^>]*>/g, '');
  }

  static encodeHtml(text: string): string {
    const rg = /[&<>"'`]/g;
    if (!text || !rg.test(text)) {
      return text;
    }

    return text.replace(rg, match => {
      switch (match) {
        case '&': return '&amp;';
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        case '\'': return '&#39;';
        case '`': return '&#96;';
        default: return match;
      }
    });
  }

  static safeString(generator: () => string): string {
    try {
      return generator();
    } catch (e) {
      console.error('the string generation failed: ', e);
      return '';
    }
  }

  static toHex(text: string): string[] {
    const hex: string[] = [];
    for (let a = 0; a < text.length; a = a + 1) {
      hex.push('0x' + text.charCodeAt(a).toString(16));
    }
    return hex;
  }

  /**
 * Function to pluralize a word based on English grammar rules.
 * @param word - The singular form of the word.
 * @returns The plural form of the word.
 */
  static pluralize(word: string): string {
    if (word.endsWith('y') && !this.isVowel(word[word.length - 2])) {
      // If the word ends with a consonant + "y", replace "y" with "ies"
      return word.slice(0, -1) + 'ies';
    } else if (word.endsWith('s') || word.endsWith('x') || word.endsWith('z') || word.endsWith('ch') || word.endsWith('sh')) {
      // If the word ends with "s", "x", "z", "ch", or "sh", add "es"
      return word + 'es';
    } else if (word.endsWith('f')) {
      // If the word ends with "f", replace "f" with "ves"
      return word.slice(0, -1) + 'ves';
    } else if (word.endsWith('fe')) {
      // If the word ends with "fe", replace "fe" with "ves"
      return word.slice(0, -2) + 'ves';
    } else if (word.endsWith('o') && !this.isVowel(word[word.length - 2])) {
      // If the word ends with a consonant + "o", add "es"
      return word + 'es';
    } else if (word.toLowerCase() === 'analysis') {
      return 'analyses';
    } else {
      // Default case: add "s"
      return word + 's';
    }
  }

  /**
 * Helper function to check if a character is a vowel.
 * @param char - The character to check.
 * @returns True if the character is a vowel, false otherwise.
 */
  static isVowel(char: string): boolean {
    return ['a', 'e', 'i', 'o', 'u'].includes(char.toLowerCase());
  }
}
